// ============================================================
// BOOKING PAGINATION COMPONENT
// ============================================================

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface BookingPaginationProps {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  onPageChange: (page: number) => void;
}

export function BookingPagination({
  currentPage,
  totalPages,
  totalResults,
  onPageChange,
}: BookingPaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
      <PageInfo currentPage={currentPage} totalPages={totalPages} totalResults={totalResults} />
      <PageControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
}

function PageInfo({
  currentPage,
  totalPages,
  totalResults,
}: {
  currentPage: number;
  totalPages: number;
  totalResults: number;
}) {
  return (
    <span className="text-xs text-slate-500">
      Page {currentPage} sur {totalPages} · {totalResults} résultats
    </span>
  );
}

interface PageControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function PageControls({ currentPage, totalPages, onPageChange }: PageControlsProps) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div className="flex items-center gap-1">
      <NavigationButton
        direction="previous"
        disabled={!canGoPrevious}
        onClick={() => onPageChange(currentPage - 1)}
      />

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <PageButton key={page} page={page} isActive={page === currentPage} onClick={() => onPageChange(page)} />
      ))}

      <NavigationButton direction="next" disabled={!canGoNext} onClick={() => onPageChange(currentPage + 1)} />
    </div>
  );
}

interface NavigationButtonProps {
  direction: 'previous' | 'next';
  disabled: boolean;
  onClick: () => void;
}

function NavigationButton({ direction, disabled, onClick }: NavigationButtonProps) {
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-white hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

interface PageButtonProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
}

function PageButton({ page, isActive, onClick }: PageButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-8 h-8 rounded border text-xs font-medium transition-colors',
        isActive
          ? 'bg-primary text-white border-primary'
          : 'border-slate-200 text-slate-600 hover:bg-white hover:border-slate-300'
      )}
    >
      {page}
    </button>
  );
}
